import React, { FunctionComponent, useEffect, useState } from "react";
import {
  BannerStatus,
  Dialog,
  BannerConsumer,
  BannerPlaceholder,
  BannerProvider,
  Button,
  ColumnType,
  Form,
  IBannerContext,
  TextInput,
  ValidationMessage
} from "@plex/react-components";
import { IViewController, useViewController } from "../ViewContext";
import "./UserDefinedSchemaEditor.scss";
import { addObjectSchema, getSchema, listSchemas } from "../NodeTypes/DataSchemas";
import { DocumentSchemaType, IDocumentObjectSchema, ObjectSchemaTags } from "../FlowDocument/FlowDocumentModel";

export interface IUserDefinedSchemasAddDialogPropsState {
  show: boolean;
}

export interface IUserDefinedSchemasAddDialogProps {
  onClose?: () => void;
}

export const UserDefinedSchemasAddDialog: FunctionComponent<IUserDefinedSchemasAddDialogProps> = (props) => {
  const { userDefinedSchemasAddDialogState }: IViewController = useViewController();
  const [nameState, setNameState] = useState<string>("");
  const [nameErrorState, setNameErrorState] = useState<string>("");
  useEffect(() => {
    if (userDefinedSchemasAddDialogState.show) {
      setNameState("");
      setNameErrorState("");
    }
  }, [userDefinedSchemasAddDialogState.show]);

  const validateName = (value: string): string => {
    if (value.trim() === "") {
      return "";
    }
    if (!/^[a-zA-Z0-9-_]+$/.test(value)) {
      return "Name must be alphanumeric, contain dashes or underscores.";
    }
    return "";
  };

  return (
    <Dialog
      show={userDefinedSchemasAddDialogState.show}
      onHide={props.onClose}
      title="User Defined Schema"
      className="user-defined-schema-dialog"
    >
      <div style={{ height: "auto" }}>
        <BannerProvider>
          <BannerConsumer>
            {(bctx: IBannerContext) => {
              return (
                <Form
                  onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                    // Do not refresh the page when pressing enter!
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <Form.Header>
                    <BannerPlaceholder style={{ marginTop: "20px", marginBottom: 0, marginLeft: 0, marginRight: 0 }} />
                  </Form.Header>
                  <Form.Body>
                    <Form.Section title="Details" layout={ColumnType.single}>
                      <Form.Row>
                        <Form.FieldPair labelText="Name" required={true}>
                          <TextInput
                            data-testid="user-defined-schema-add-name-input"
                            value={nameState}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              const newValue = e.target.value;
                              setNameState(newValue);
                              setNameErrorState(validateName(newValue));
                            }}
                            placeholder="Enter name"
                          />
                          <ValidationMessage message={nameErrorState} />
                        </Form.FieldPair>
                      </Form.Row>
                    </Form.Section>
                  </Form.Body>
                  <Form.Footer>
                    <Form.Row>
                      <Button variance={"link"} as={"button"} type="button" onClick={props.onClose}>
                        Cancel
                      </Button>
                      <Button
                        data-testid="user-defined-schema-add-form-save"
                        variance={"primary"}
                        as={"button"}
                        type="button"
                        disabled={nameState.trim() === "" || nameErrorState !== ""}
                        onClick={() => {
                          const newSchemaId = `${ObjectSchemaTags.userDefinedSchema}_${nameState}`;
                          if (getSchema(newSchemaId)?.id === newSchemaId) {
                            bctx.addMessage(
                              "A schema with this name already exists. Please choose a different name.",
                              BannerStatus.warning
                            );
                            return;
                          }

                          const userDefinedSchemas = listSchemas({ userDefinedOnly: true, includeLists: false });
                          if (
                            userDefinedSchemas.some((schema) => schema.name === nameState && schema.id !== newSchemaId)
                          ) {
                            bctx.addMessage(
                              "A schema with this name already exists. Please choose a different name.",
                              BannerStatus.warning
                            );
                            return;
                          }

                          addObjectSchema(
                            {
                              id: newSchemaId,
                              schemaType: DocumentSchemaType.object,
                              properties: {},
                              metadata: {
                                userDefinedSchemaId: newSchemaId,
                                userDefinedSchemaName: nameState
                              },
                              tags: [ObjectSchemaTags.userDefinedSchema]
                            } as IDocumentObjectSchema,
                            ObjectSchemaTags.userDefinedSchema,
                            nameState
                          );
                          if (props.onClose) {
                            props.onClose();
                          }
                        }}
                      >
                        Ok
                      </Button>
                    </Form.Row>
                  </Form.Footer>
                </Form>
              );
            }}
          </BannerConsumer>
        </BannerProvider>
      </div>
    </Dialog>
  );
};
